import { useState, useEffect, useMemo } from 'react'
import { useNavigate, Link, useParams, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Plus, Trash2, Loader2, FileText, Mail, Download, Send, Check } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { quotesApi, invoiceTemplatesApi, clientsApi, taxMasterApi, feesMasterApi, agencyConfigApi, productMasterApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Separator } from '@/components/ui/separator'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import type { CreateQuoteRequest, LineItemRequest, InvoiceTemplate, TaxMasterListItem, FeesMasterListItem, QuoteStatus, AdditionalContact, BusinessResponse } from '@/types'
import { formatCurrency, formatDateForInput } from '@/lib/utils'

// List of Indian Union Territories
const UNION_TERRITORIES = [
  'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry',
]

const isUnionTerritory = (state: string | undefined): boolean => {
  if (!state) return false
  return UNION_TERRITORIES.some(ut => ut.toLowerCase() === state.toLowerCase())
}

const statusColors: Record<QuoteStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-800',
  SENT: 'bg-blue-100 text-blue-800',
  ACCEPTED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
  EXPIRED: 'bg-orange-100 text-orange-800',
  CONVERTED: 'bg-purple-100 text-purple-800',
}

const statusLabels: Record<QuoteStatus, string> = {
  DRAFT: 'Draft', SENT: 'Sent', ACCEPTED: 'Accepted',
  REJECTED: 'Rejected', EXPIRED: 'Expired', CONVERTED: 'Converted',
}

interface CreateQuotePageProps {
  mode?: 'create' | 'view' | 'edit' | 'convert'
}

export default function CreateQuotePage({ mode: propMode }: CreateQuotePageProps = {}) {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const isViewMode = propMode === 'view' || (!!id && propMode !== 'edit' && propMode !== 'convert')
  const isEditMode = propMode === 'edit'
  const isCreateMode = !id && !propMode
  const isConvertMode = propMode === 'convert'

  // Get client_id from URL query params (for chatbot integration)
  const preselectedClientId = searchParams.get('client_id')

  // Fetch templates (QUOTE type only)
  const { data: templates = [] } = useQuery({
    queryKey: ['billingTemplates', 'QUOTE'],
    queryFn: () => invoiceTemplatesApi.getAll('QUOTE'),
  })

  // Fetch default template (QUOTE type)
  const { data: defaultTemplate } = useQuery({
    queryKey: ['billingTemplates', 'QUOTE', 'default'],
    queryFn: () => invoiceTemplatesApi.getDefault('QUOTE'),
  })

  // Fetch clients
  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: clientsApi.getAllLegacy,
  })

  // Fetch active taxes
  const { data: taxes = [] } = useQuery({
    queryKey: ['taxes', 'active'],
    queryFn: () => taxMasterApi.getAll(true),
  })

  // Fetch active fees
  const { data: feesList = [] } = useQuery({
    queryKey: ['fees', 'active'],
    queryFn: () => feesMasterApi.getAll(true),
  })

  // Fetch active products
  const { data: products = [] } = useQuery({
    queryKey: ['products', 'active'],
    queryFn: () => productMasterApi.getAll(true),
  })

  // Fetch agency configuration
  const { data: agencyConfig } = useQuery({
    queryKey: ['agencyConfig'],
    queryFn: agencyConfigApi.get,
  })

  // Fetch existing quote if editing/viewing
  const { data: existingQuote, isLoading: isLoadingQuote } = useQuery({
    queryKey: ['quote', id],
    queryFn: () => quotesApi.getById(id!),
    enabled: !!id,
  })

  const [formData, setFormData] = useState<CreateQuoteRequest>({
    client_id: '',
    valid_until: formatDateForInput(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()),
    currency: 'USD',
    discount_value: 0,
    line_items: [{ description: '', quantity: 1, unit_price: 0 }],
  })

  const [selectedTemplate, setSelectedTemplate] = useState<InvoiceTemplate | null>(null)
  const [selectedTax, setSelectedTax] = useState<TaxMasterListItem | null>(null)
  const [selectedFees, setSelectedFees] = useState<FeesMasterListItem | null>(null)

  // Convert to invoice dialog state
  const [isConvertDialogOpen, setIsConvertDialogOpen] = useState(false)
  const [convertDueDate, setConvertDueDate] = useState(formatDateForInput(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()))
  const [isConverting, setIsConverting] = useState(false)

  // Send quote dialog state
  const [isSendDialogOpen, setIsSendDialogOpen] = useState(false)
  const [selectedEmails, setSelectedEmails] = useState<string[]>([])
  const [emailSubject, setEmailSubject] = useState('')
  const [emailMessage, setEmailMessage] = useState('')
  const [isSendingQuote, setIsSendingQuote] = useState(false)
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)

  // Set default template when loaded
  useEffect(() => {
    if (defaultTemplate && !selectedTemplate && isCreateMode) {
      setSelectedTemplate(defaultTemplate)
      setFormData(prev => ({
        ...prev,
        template_id: defaultTemplate.template_id,
        notes: defaultTemplate.default_notes || prev.notes,
        payment_terms: defaultTemplate.default_payment_terms || prev.payment_terms,
      }))
    }
  }, [defaultTemplate, selectedTemplate, isCreateMode])

  // Set preselected client from URL query params (for chatbot integration)
  useEffect(() => {
    if (preselectedClientId && isCreateMode && clients.length > 0) {
      const client = clients.find(c => c.client_id === preselectedClientId)
      if (client) {
        setFormData(prev => ({
          ...prev,
          client_id: preselectedClientId,
          currency: client.currency || prev.currency,
        }))
      }
    }
  }, [preselectedClientId, isCreateMode, clients])

  // Populate form with existing quote data
  useEffect(() => {
    if (existingQuote) {
      setFormData({
        template_id: existingQuote.template_id,
        client_id: existingQuote.client_id,
        quote_date: formatDateForInput(existingQuote.quote_date),
        valid_until: formatDateForInput(existingQuote.valid_until),
        currency: existingQuote.currency,
        tax_id: existingQuote.tax_id,
        tax_rate: existingQuote.tax_rate,
        is_gst_invoice: existingQuote.is_gst_invoice,
        cgst_amount: existingQuote.cgst_amount,
        sgst_amount: existingQuote.sgst_amount,
        igst_amount: existingQuote.igst_amount,
        utgst_amount: existingQuote.utgst_amount,
        fees_id: existingQuote.fees_id,
        fees_amount: existingQuote.fees_amount,
        discount_type: existingQuote.discount_type,
        discount_value: existingQuote.discount_value,
        notes: existingQuote.notes,
        payment_terms: existingQuote.payment_terms,
        line_items: existingQuote.line_items.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          unit: item.unit,
          tax_rate: item.tax_rate,
          discount_amount: item.discount_amount,
          sort_order: item.sort_order,
        })),
      })
      if (existingQuote.template) {
        setSelectedTemplate(existingQuote.template)
      }
      if (existingQuote.tax_id && taxes.length > 0) {
        const tax = taxes.find(t => t.tax_id === existingQuote.tax_id)
        if (tax) setSelectedTax(tax)
      }
      if (existingQuote.fees_id && feesList.length > 0) {
        const fees = feesList.find(f => f.fees_id === existingQuote.fees_id)
        if (fees) setSelectedFees(fees)
      }
    }
  }, [existingQuote, taxes, feesList])

  // Open convert dialog if in convert mode
  useEffect(() => {
    if (isConvertMode && existingQuote) {
      setIsConvertDialogOpen(true)
    }
  }, [isConvertMode, existingQuote])

  const selectedClient = useMemo(() => {
    return clients.find(c => c.client_id === formData.client_id)
  }, [clients, formData.client_id])

  // Fetch full client details (with additional contacts and businesses) when client is selected
  const clientIdToFetch = formData.client_id || existingQuote?.client_id || ''
  const { data: selectedClientDetails } = useQuery({
    queryKey: ['client', clientIdToFetch],
    queryFn: () => clientsApi.getById(clientIdToFetch),
    enabled: !!clientIdToFetch,
  })

  // Collect all available emails from client and businesses
  const availableEmails = useMemo(() => {
    if (!selectedClientDetails) return []

    const emails: { email: string; label: string; source: 'client' | 'business' | 'contact' }[] = []

    // Primary client email
    if (selectedClientDetails.client_email) {
      emails.push({
        email: selectedClientDetails.client_email,
        label: `${selectedClientDetails.client_name} (Primary)`,
        source: 'client'
      })
    }

    // Additional contacts (emails only)
    if (selectedClientDetails.additional_contacts) {
      selectedClientDetails.additional_contacts
        .filter((contact: AdditionalContact) => contact.contact_type === 'EMAIL')
        .forEach((contact: AdditionalContact) => {
          emails.push({
            email: contact.contact_value,
            label: contact.contact_label || `Additional Email`,
            source: 'contact'
          })
        })
    }

    // Business emails
    if (selectedClientDetails.businesses) {
      selectedClientDetails.businesses.forEach((business: BusinessResponse) => {
        if (business.business_email) {
          emails.push({
            email: business.business_email,
            label: `${business.business_name}`,
            source: 'business'
          })
        }
      })
    }

    return emails
  }, [selectedClientDetails])

  // Calculate totals
  const calculations = useMemo(() => {
    const subtotal = formData.line_items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0)
    const discountAmount = formData.discount_value || 0
    const taxableAmount = subtotal - discountAmount

    let taxAmount = 0
    let cgstAmount = 0, sgstAmount = 0, igstAmount = 0, utgstAmount = 0

    if (selectedTax && formData.is_gst_invoice) {
      const clientState = selectedClient?.state
      const agencyState = agencyConfig?.state
      const isSameState = clientState && agencyState && clientState.toLowerCase() === agencyState.toLowerCase()
      const isClientUT = isUnionTerritory(clientState)
      const halfRate = selectedTax.tax_rate / 2

      if (isSameState) {
        if (isClientUT) {
          cgstAmount = taxableAmount * halfRate / 100
          utgstAmount = taxableAmount * halfRate / 100
          taxAmount = cgstAmount + utgstAmount
        } else {
          cgstAmount = taxableAmount * halfRate / 100
          sgstAmount = taxableAmount * halfRate / 100
          taxAmount = cgstAmount + sgstAmount
        }
      } else {
        igstAmount = taxableAmount * selectedTax.tax_rate / 100
        taxAmount = igstAmount
      }
    } else if (selectedTax) {
      taxAmount = taxableAmount * selectedTax.tax_rate / 100
    }

    const feesAmount = selectedFees ? (selectedFees.fees_type === 'PERCENTAGE' ? taxableAmount * selectedFees.fees_percentage / 100 : selectedFees.fees_amount) : 0
    const totalAmount = taxableAmount + taxAmount + feesAmount

    return { subtotal, discountAmount, taxableAmount, taxAmount, cgstAmount, sgstAmount, igstAmount, utgstAmount, feesAmount, totalAmount }
  }, [formData.line_items, formData.discount_value, formData.is_gst_invoice, selectedTax, selectedFees, selectedClient, agencyConfig])

  // Create mutation
  const createMutation = useMutation({
    mutationFn: quotesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
      toast.success(t('quotes.quoteCreated'))
      navigate('/billing/quotes')
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('quotes.failedToCreate')
      toast.error(errorMessage)
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => quotesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
      queryClient.invalidateQueries({ queryKey: ['quote', id] })
      toast.success(t('quotes.quoteUpdated'))
      navigate(`/billing/quotes/${id}`)
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('quotes.failedToUpdate')
      toast.error(errorMessage)
    },
  })

  // Convert to invoice mutation
  const convertMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { due_date: string } }) => quotesApi.convertToInvoice(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      toast.success(t('quotes.convertedToInvoice', { invoiceNumber: data.invoice_number }))
      navigate(`/billing/invoices/${data.invoice_id}`)
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('quotes.failedToConvert')
      toast.error(errorMessage)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.client_id) {
      toast.error(t('quotes.pleaseSelectClient'))
      return
    }
    if (formData.line_items.length === 0 || !formData.line_items.some(item => item.description)) {
      toast.error(t('quotes.pleaseAddLineItem'))
      return
    }

    const submitData = {
      ...formData,
      tax_rate: selectedTax?.tax_rate || 0,
      cgst_amount: calculations.cgstAmount,
      sgst_amount: calculations.sgstAmount,
      igst_amount: calculations.igstAmount,
      utgst_amount: calculations.utgstAmount,
      fees_amount: calculations.feesAmount,
    }

    if (isEditMode && id) {
      updateMutation.mutate({ id, data: submitData })
    } else {
      createMutation.mutate(submitData)
    }
  }

  const handleConvertToInvoice = () => {
    if (!id) return
    setIsConverting(true)
    convertMutation.mutate({ id, data: { due_date: convertDueDate } }, {
      onSettled: () => setIsConverting(false)
    })
  }

  // Open send quote dialog
  const openSendDialog = () => {
    // Pre-select primary client email if available
    if (availableEmails.length > 0) {
      const primaryEmail = availableEmails.find(e => e.source === 'client')
      if (primaryEmail) {
        setSelectedEmails([primaryEmail.email])
      }
    }
    // Set default subject
    if (existingQuote) {
      setEmailSubject(`Quote ${existingQuote.quote_number} from ${existingQuote.template?.company_name || 'Your Company'}`)
    }
    setIsSendDialogOpen(true)
  }

  // Handle email selection toggle
  const toggleEmailSelection = (email: string) => {
    setSelectedEmails(prev =>
      prev.includes(email)
        ? prev.filter(e => e !== email)
        : [...prev, email]
    )
  }

  // Handle send quote
  const handleSendQuote = async () => {
    if (!id || selectedEmails.length === 0) return

    setIsSendingQuote(true)
    try {
      const result = await quotesApi.sendQuote(id, {
        to_emails: selectedEmails,
        subject: emailSubject || undefined,
        message: emailMessage || undefined,
      })
      toast.success(result.message || t('quotes.quoteSent'))
      setIsSendDialogOpen(false)
      setSelectedEmails([])
      setEmailSubject('')
      setEmailMessage('')
      queryClient.invalidateQueries({ queryKey: ['quote', id] })
      queryClient.invalidateQueries({ queryKey: ['quotes'] })
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('quotes.failedToSend'))
    } finally {
      setIsSendingQuote(false)
    }
  }

  // Handle download PDF
  const handleDownloadPdf = async () => {
    if (!id) return
    setIsDownloadingPdf(true)
    try {
      const blob = await quotesApi.downloadPdf(id)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `quote_${existingQuote?.quote_number || id}.pdf`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      toast.success(t('quotes.pdfDownloaded'))
    } catch (error) {
      console.error('Failed to download PDF:', error)
      toast.error(t('quotes.failedToDownloadPdf'))
    } finally {
      setIsDownloadingPdf(false)
    }
  }

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: '', quantity: 1, unit_price: 0 }],
    }))
  }

  const removeLineItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index),
    }))
  }

  const updateLineItem = (index: number, field: keyof LineItemRequest, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => i === index ? { ...item, [field]: value } : item),
    }))
  }

  const handleProductSelect = (index: number, productId: string) => {
    const product = products.find(p => p.product_id === productId)
    if (product) {
      const newLineItems = formData.line_items.map((item, i) => i === index ? {
        ...item,
        product_id: productId,
        description: product.description || product.name,
        unit_price: product.price,
        unit: product.unit,
        tax_rate: product.tax_rate || 0,
      } : item)

      // Build updates for formData
      const formUpdates: Partial<typeof formData> = {
        line_items: newLineItems,
      }

      // If product has tax, set the quote-level tax
      if (product.tax_id) {
        const tax = taxes.find(t => t.tax_id === product.tax_id)
        if (tax) {
          setSelectedTax(tax)
          formUpdates.tax_id = product.tax_id
          formUpdates.tax_rate = tax.tax_rate
          formUpdates.is_gst_invoice = tax.is_gst
        }
      }

      // If product has discount, set the quote-level discount
      if (product.discount_type && product.discount_value) {
        formUpdates.discount_type = product.discount_type
        formUpdates.discount_value = product.discount_value
      }

      // If product has fees, set the quote-level fees
      if (product.fees_id) {
        const fees = feesList.find(f => f.fees_id === product.fees_id)
        if (fees) {
          setSelectedFees(fees)
          formUpdates.fees_id = product.fees_id
        }
      }

      setFormData(prev => ({ ...prev, ...formUpdates }))
    } else if (productId === 'none') {
      setFormData(prev => ({
        ...prev,
        line_items: prev.line_items.map((item, i) => i === index ? {
          ...item,
          product_id: undefined,
        } : item),
      }))
    }
  }

  if (id && isLoadingQuote) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const pageTitle = isViewMode ? t('quotes.viewQuote') : isEditMode ? t('quotes.editQuote') : isConvertMode ? t('quotes.convertQuote') : t('quotes.createQuote')
  const isSubmitting = createMutation.isPending || updateMutation.isPending

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/billing/quotes">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">{pageTitle}</h1>
          <p className="text-muted-foreground">
            {isViewMode ? t('quotes.viewQuoteDetails') : isEditMode ? t('quotes.updateQuoteInfo') : t('quotes.fillDetailsToCreate')}
          </p>
        </div>
        {isViewMode && existingQuote && (
          <div className="flex items-center gap-2">
            <Badge className={statusColors[existingQuote.status]} variant="secondary">
              {statusLabels[existingQuote.status]}
            </Badge>
            {(existingQuote.status === 'ACCEPTED' || existingQuote.status === 'SENT') && !existingQuote.converted_to_invoice_id && (
              <Button onClick={() => setIsConvertDialogOpen(true)}>
                <FileText className="mr-2 h-4 w-4" />
                {t('quotes.convertToInvoice')}
              </Button>
            )}
            {(existingQuote.status === 'DRAFT' || existingQuote.status === 'SENT') && (
              <Link to={`/billing/quotes/${id}/edit`}>
                <Button variant="outline">{t('quotes.editQuote')}</Button>
              </Link>
            )}
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Quote Details Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-primary border-b pb-2">{t('quotes.quoteDetails')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="client">{t('quotes.client')} {!isViewMode && <span className="text-destructive">*</span>}</Label>
              <Select value={formData.client_id} onValueChange={(value) => setFormData(prev => ({ ...prev, client_id: value, business_id: undefined }))} disabled={isViewMode}>
                <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"><SelectValue placeholder={t('quotes.selectClient')} /></SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.client_id} value={client.client_id}>{client.client_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="business">{t('quotes.business')}</Label>
              <Select
                value={formData.business_id || 'none'}
                onValueChange={(value) => setFormData(prev => ({ ...prev, business_id: value === 'none' ? undefined : value }))}
                disabled={isViewMode || !formData.client_id || !selectedClientDetails?.businesses?.length}
              >
                <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"><SelectValue placeholder={t('quotes.selectBusiness')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('common.none')}</SelectItem>
                  {selectedClientDetails?.businesses?.map((business) => (
                    <SelectItem key={business.business_id} value={business.business_id}>{business.business_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="template">{t('quotes.template')}</Label>
              <Select value={formData.template_id || ''} onValueChange={(value) => {
                const template = templates.find(t => t.template_id === value)
                setSelectedTemplate(template || null)
                setFormData(prev => ({ ...prev, template_id: value || undefined }))
              }} disabled={isViewMode}>
                <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"><SelectValue placeholder={t('quotes.selectTemplate')} /></SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.template_id} value={template.template_id}>{template.template_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="quote_date">{t('quotes.quoteDate')}</Label>
              <Input id="quote_date" type="date" value={formData.quote_date || formatDateForInput(new Date().toISOString())} onChange={(e) => setFormData(prev => ({ ...prev, quote_date: e.target.value }))} disabled={isViewMode} className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="valid_until">{t('quotes.validUntil')} {!isViewMode && <span className="text-destructive">*</span>}</Label>
              <Input id="valid_until" type="date" value={formData.valid_until} onChange={(e) => setFormData(prev => ({ ...prev, valid_until: e.target.value }))} disabled={isViewMode} className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency">{t('quotes.currency')}</Label>
              <Select value={formData.currency} onValueChange={(value) => setFormData(prev => ({ ...prev, currency: value }))} disabled={isViewMode}>
                <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                  <SelectItem value="INR">INR</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Line Items Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-primary border-b pb-2">{t('quotes.lineItems')}</h3>
          <Table>
            <TableHeader>
              <TableRow>
                {!isViewMode && <TableHead className="w-[180px]">{t('quotes.product')}</TableHead>}
                <TableHead className="w-[35%]">{t('quotes.description')}</TableHead>
                <TableHead className="w-[12%]">{t('quotes.quantity')}</TableHead>
                <TableHead className="w-[15%]">{t('quotes.unitPrice')}</TableHead>
                <TableHead className="w-[12%] text-right">{t('quotes.amount')}</TableHead>
                {!isViewMode && <TableHead className="w-[50px]"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {formData.line_items.map((item, index) => (
                <TableRow key={index}>
                  {!isViewMode && (
                    <TableCell>
                      <Select
                        value={item.product_id || 'none'}
                        onValueChange={(value) => handleProductSelect(index, value)}
                      >
                        <SelectTrigger className="w-[170px] bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700">
                          <SelectValue placeholder={t('quotes.selectProduct')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">{t('quotes.manualEntry')}</SelectItem>
                          {products.map((product) => (
                            <SelectItem key={product.product_id} value={product.product_id}>
                              {product.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                  )}
                  <TableCell>
                    <Input placeholder={t('quotes.itemDescription')} value={item.description} onChange={(e) => updateLineItem(index, 'description', e.target.value)} disabled={isViewMode} className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700" />
                  </TableCell>
                  <TableCell>
                    <Input type="number" min="0" step="0.01" value={item.quantity} onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)} disabled={isViewMode} className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700" />
                  </TableCell>
                  <TableCell>
                    <Input type="number" min="0" step="0.01" value={item.unit_price} onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)} disabled={isViewMode} className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700" />
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(item.quantity * item.unit_price, formData.currency)}
                  </TableCell>
                  {!isViewMode && (
                    <TableCell>
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeLineItem(index)} disabled={formData.line_items.length === 1}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {!isViewMode && (
            <Button type="button" variant="outline" className="mt-4" onClick={addLineItem}>
              <Plus className="mr-2 h-4 w-4" />
              {t('quotes.addLineItem')}
            </Button>
          )}
        </div>

        {/* Tax & Fees Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-primary border-b pb-2">{t('quotes.taxAndFees')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>{t('quotes.tax')}</Label>
              <Select value={formData.tax_id || 'NONE'} onValueChange={(value) => {
                const tax = value === 'NONE' ? null : taxes.find(t => t.tax_id === value)
                setSelectedTax(tax || null)
                setFormData(prev => ({ ...prev, tax_id: value === 'NONE' ? undefined : value }))
              }} disabled={isViewMode}>
                <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"><SelectValue placeholder={t('quotes.selectTax')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">{t('quotes.noTax')}</SelectItem>
                  {taxes.map((tax) => (
                    <SelectItem key={tax.tax_id} value={tax.tax_id}>{tax.tax_name} ({tax.tax_rate}%)</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedTax && selectedTax.is_gst && (
                <div className="flex items-center space-x-2 mt-2">
                  <input type="checkbox" id="is_gst" checked={formData.is_gst_invoice || false} onChange={(e) => setFormData(prev => ({ ...prev, is_gst_invoice: e.target.checked }))} disabled={isViewMode} className="h-4 w-4" />
                  <Label htmlFor="is_gst">{t('quotes.applyGst')}</Label>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>{t('quotes.fees')}</Label>
              <Select value={formData.fees_id || 'NONE'} onValueChange={(value) => {
                const fees = value === 'NONE' ? null : feesList.find(f => f.fees_id === value)
                setSelectedFees(fees || null)
                setFormData(prev => ({ ...prev, fees_id: value === 'NONE' ? undefined : value }))
              }} disabled={isViewMode}>
                <SelectTrigger className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700"><SelectValue placeholder={t('quotes.selectFees')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">{t('quotes.noFees')}</SelectItem>
                  {feesList.map((fees) => (
                    <SelectItem key={fees.fees_id} value={fees.fees_id}>{fees.fees_name} ({fees.fees_type === 'PERCENTAGE' ? `${fees.fees_percentage}%` : formatCurrency(fees.fees_amount, formData.currency)})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('quotes.discount')}</Label>
              <Input type="number" min="0" step="0.01" value={formData.discount_value || 0} onChange={(e) => setFormData(prev => ({ ...prev, discount_value: parseFloat(e.target.value) || 0 }))} disabled={isViewMode} className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700" />
            </div>
          </div>
        </div>

        {/* Additional Information Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-primary border-b pb-2">{t('quotes.additionalInfo')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="notes">{t('quotes.notes')}</Label>
              <Textarea id="notes" placeholder={t('quotes.notesPlaceholder')} value={formData.notes || ''} onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))} disabled={isViewMode} className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="payment_terms">{t('quotes.paymentTerms')}</Label>
              <Textarea id="payment_terms" placeholder={t('quotes.paymentTermsPlaceholder')} value={formData.payment_terms || ''} onChange={(e) => setFormData(prev => ({ ...prev, payment_terms: e.target.value }))} disabled={isViewMode} className="bg-white dark:bg-zinc-900 border-gray-300 dark:border-zinc-700" />
            </div>
          </div>
        </div>

        {/* Summary Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-primary border-b pb-2">{t('quotes.summary')}</h3>
          <Card className="max-w-md">
            <CardContent className="pt-6 space-y-2">
              <div className="flex justify-between"><span>{t('quotes.subtotal')}</span><span>{formatCurrency(calculations.subtotal, formData.currency)}</span></div>
              {calculations.discountAmount > 0 && (
                <div className="flex justify-between text-red-600"><span>{t('quotes.discount')}</span><span>-{formatCurrency(calculations.discountAmount, formData.currency)}</span></div>
              )}
              {formData.is_gst_invoice && selectedTax ? (
                <>
                  {calculations.cgstAmount > 0 && <div className="flex justify-between"><span>CGST ({selectedTax.tax_rate / 2}%)</span><span>{formatCurrency(calculations.cgstAmount, formData.currency)}</span></div>}
                  {calculations.sgstAmount > 0 && <div className="flex justify-between"><span>SGST ({selectedTax.tax_rate / 2}%)</span><span>{formatCurrency(calculations.sgstAmount, formData.currency)}</span></div>}
                  {calculations.utgstAmount > 0 && <div className="flex justify-between"><span>UTGST ({selectedTax.tax_rate / 2}%)</span><span>{formatCurrency(calculations.utgstAmount, formData.currency)}</span></div>}
                  {calculations.igstAmount > 0 && <div className="flex justify-between"><span>IGST ({selectedTax.tax_rate}%)</span><span>{formatCurrency(calculations.igstAmount, formData.currency)}</span></div>}
                </>
              ) : calculations.taxAmount > 0 && (
                <div className="flex justify-between"><span>{t('quotes.tax')} ({selectedTax?.tax_rate || 0}%)</span><span>{formatCurrency(calculations.taxAmount, formData.currency)}</span></div>
              )}
              {calculations.feesAmount > 0 && (
                <div className="flex justify-between"><span>{t('quotes.fees')}</span><span>{formatCurrency(calculations.feesAmount, formData.currency)}</span></div>
              )}
              <Separator />
              <div className="flex justify-between font-bold text-lg"><span>{t('quotes.total')}</span><span>{formatCurrency(calculations.totalAmount, formData.currency)}</span></div>
            </CardContent>
          </Card>
        </div>

        {/* Form Actions */}
        {!isViewMode && (
          <div className="flex justify-center pt-4">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditMode ? t('quotes.updateQuote') : t('quotes.createQuote')}
            </Button>
          </div>
        )}

        {/* Action Buttons - only show in view mode */}
        {isViewMode && id && (
          <div className="flex justify-center gap-4 pt-4">
            <Button
              type="button"
              onClick={openSendDialog}
            >
              <Mail className="mr-2 h-4 w-4" />
              {t('quotes.sendQuote')}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={isDownloadingPdf}
              onClick={handleDownloadPdf}
            >
              {isDownloadingPdf ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {t('quotes.downloadPdf')}
            </Button>
          </div>
        )}
      </form>

      {/* Convert to Invoice Dialog */}
      <Dialog open={isConvertDialogOpen} onOpenChange={setIsConvertDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('quotes.convertToInvoice')}</DialogTitle>
            <DialogDescription>
              {t('quotes.convertDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="due_date">{t('quotes.invoiceDueDate')} *</Label>
              <Input id="due_date" type="date" value={convertDueDate} onChange={(e) => setConvertDueDate(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsConvertDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleConvertToInvoice} disabled={isConverting}>
              {isConverting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('quotes.convertToInvoice')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Quote Dialog */}
      <Dialog open={isSendDialogOpen} onOpenChange={setIsSendDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              {t('quotes.sendQuote')}
            </DialogTitle>
            <DialogDescription>
              {t('quotes.selectRecipientsMessage')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Email Recipients */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">{t('quotes.sendTo')}</Label>
              {availableEmails.length > 0 ? (
                <div className="space-y-2 max-h-40 overflow-y-auto border rounded-md p-2">
                  {availableEmails.map((emailInfo, index) => (
                    <div
                      key={index}
                      className="flex items-center space-x-3 p-2 hover:bg-muted rounded-md cursor-pointer"
                      onClick={() => toggleEmailSelection(emailInfo.email)}
                    >
                      <Checkbox
                        checked={selectedEmails.includes(emailInfo.email)}
                        onCheckedChange={() => toggleEmailSelection(emailInfo.email)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{emailInfo.email}</p>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">{emailInfo.label}</span>
                          <Badge variant="outline" className="text-xs">
                            {emailInfo.source === 'client' ? t('clients.client') :
                             emailInfo.source === 'business' ? t('clients.business') : t('clients.contact')}
                          </Badge>
                        </div>
                      </div>
                      {selectedEmails.includes(emailInfo.email) && (
                        <Check className="h-4 w-4 text-primary" />
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t('quotes.noEmailsFound')}
                </p>
              )}
              {selectedEmails.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {selectedEmails.length} {t('quotes.recipientsSelected')}
                </p>
              )}
            </div>

            {/* Email Subject */}
            <div className="space-y-2">
              <Label htmlFor="email-subject">{t('quotes.subjectOptional')}</Label>
              <Input
                id="email-subject"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder={`${t('quotes.quote')} ${existingQuote?.quote_number || ''}`}
              />
            </div>

            {/* Email Message */}
            <div className="space-y-2">
              <Label htmlFor="email-message">{t('quotes.messageOptional')}</Label>
              <Textarea
                id="email-message"
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder={t('quotes.messagePlaceholder')}
                rows={4}
              />
              <p className="text-xs text-muted-foreground">
                {t('quotes.leaveBlankDefault')}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsSendDialogOpen(false)}
              disabled={isSendingQuote}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSendQuote}
              disabled={selectedEmails.length === 0 || isSendingQuote}
            >
              {isSendingQuote ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('quotes.sending')}
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  {t('quotes.sendQuote')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

